/*
 * <cart-discount> — discount code entry for the cart drawer.
 *
 * Applies / removes codes through the Cart AJAX API (`discount` parameter on
 * /cart/update.js, which takes the FULL comma separated list of codes to keep).
 *
 * Shopify silently ignores codes that are invalid, expired or not applicable to
 * the current cart, so success is confirmed by re-rendering the drawer from the
 * `cart-drawer` section and checking whether the code came back in
 * `cart.discount_applications`, which Liquid writes to data-applied-codes.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-discount-form]');
    this.input = this.querySelector('[data-discount-input]');
    this.errorEl = this.querySelector('[data-discount-error]');
    this.submitButton = this.querySelector('[data-discount-submit]');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.querySelectorAll('[data-discount-remove]').forEach((button) => {
      button.addEventListener('click', this.onRemove.bind(this));
    });
  }

  /** Codes currently applied to the cart, as rendered by Liquid. */
  get appliedCodes() {
    const raw = this.dataset.appliedCodes || '';
    return raw.split(',').map((code) => code.trim()).filter(Boolean);
  }

  onSubmit(event) {
    event.preventDefault();

    const code = (this.input.value || '').trim();
    if (!code) return;

    const alreadyApplied = this.appliedCodes.some(
      (applied) => applied.toLowerCase() === code.toLowerCase()
    );

    if (alreadyApplied) {
      this.showError(this.dataset.errorDuplicate);
      return;
    }

    this.submit([...this.appliedCodes, code], code);
  }

  onRemove(event) {
    event.preventDefault();

    const code = event.currentTarget.dataset.discountRemove;
    const remaining = this.appliedCodes.filter(
      (applied) => applied.toLowerCase() !== code.toLowerCase()
    );

    this.submit(remaining, null);
  }

  /**
   * @param {string[]} codes    the complete list of codes the cart should end up with
   * @param {?string}  expected code whose presence confirms the update worked
   */
  submit(codes, expected) {
    this.clearError();
    this.setLoading(true);

    const body = JSON.stringify({ discount: codes.join(',') });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then(() => this.refreshDrawer(expected))
      .catch((error) => {
        console.error(error);
        this.setLoading(false);
        this.showError(window.cartStrings ? window.cartStrings.error : this.dataset.errorInvalid);
      });
  }

  /**
   * Re-renders the drawer from Liquid, then reports the outcome on the fresh
   * instance of this element (the old one is gone once the footer is swapped).
   */
  refreshDrawer(expected) {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });

        const refreshed = document.querySelector('cart-discount');
        if (!refreshed) return;

        // Nothing was swapped out (unexpected markup): release this instance.
        if (refreshed === this) this.setLoading(false);

        if (expected) {
          const applied = refreshed.appliedCodes.some(
            (code) => code.toLowerCase() === expected.toLowerCase()
          );

          if (!applied) {
            // The code was rejected: keep it in the field so it can be corrected.
            const input = refreshed.querySelector('[data-discount-input]');
            if (input) input.value = expected;
            refreshed.showError(refreshed.dataset.errorInvalid);
            input?.focus();
          }
        }
      })
      .catch((error) => {
        console.error(error);
        this.setLoading(false);
        this.showError(window.cartStrings ? window.cartStrings.error : this.dataset.errorInvalid);
      });
  }

  setLoading(isLoading) {
    this.classList.toggle('cart-discount--loading', isLoading);
    if (this.submitButton) this.submitButton.disabled = isLoading;
    if (this.input) this.input.disabled = isLoading;
  }

  showError(message) {
    if (!this.errorEl || !message) return;

    // The field collapses again after a re-render, so make sure it is visible.
    const details = this.querySelector('details');
    if (details) details.open = true;

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (this.input) this.input.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
    if (this.input) this.input.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
